import type {
  LightspeedCustomFieldMapping,
  LightspeedCustomFieldOption,
  LightspeedSpecOption,
} from '../../services/integrationApi';

/** One saved row of the spec → Lightspeed custom field map. */
export interface FieldRow {
  specLabel: string;
  customFieldId: string;
}

/** Slugs with a suggestion and nothing mapped yet: what "Accept all" would fill in. */
export function pendingCategorySuggestions(
  slugs: string[],
  suggestions: Record<string, string>,
  categoryMap: Record<string, string>,
): string[] {
  return slugs.filter((slug) => suggestions[slug] && !categoryMap[slug]);
}

const norm = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * A writable, unmapped Lightspeed field whose name reads as a catalog spec the selection carries
 * (and whose spec is not mapped elsewhere) is suggested for that spec.
 */
export function pendingFieldSuggestions(
  fields: LightspeedCustomFieldOption[],
  specs: LightspeedSpecOption[],
  rows: FieldRow[],
): FieldRow[] {
  const mappedFields = new Set(rows.map((r) => r.customFieldId));
  const usedSpecs = new Set(rows.map((r) => r.specLabel.toLowerCase()));
  const specByName = new Map(specs.map((s) => [norm(s.label), s.label]));
  const out: FieldRow[] = [];
  for (const field of fields) {
    if (!field.writable || mappedFields.has(field.customFieldID)) continue;
    const spec = specByName.get(norm(field.name));
    if (!spec || usedSpecs.has(spec.toLowerCase())) continue;
    usedSpecs.add(spec.toLowerCase());
    out.push({ customFieldId: field.customFieldID, specLabel: spec });
  }
  return out;
}

export function sameCategoryMap(a: Record<string, string>, b: Record<string, string>): boolean {
  return [...Object.keys(a), ...Object.keys(b)].every((k) => (a[k] || '') === (b[k] || ''));
}

/**
 * Saved rows whose Lightspeed field was deleted, archived, or is no longer writable.
 * The mapping table only lists current writable fields, so these would otherwise be
 * submitted invisibly and rejected.
 */
export function staleFieldRows(
  fields: Array<Pick<LightspeedCustomFieldOption, 'customFieldID' | 'writable'>>,
  rows: FieldRow[],
): FieldRow[] {
  const writable = new Set(fields.filter((field) => field.writable).map((field) => field.customFieldID));
  return rows.filter((row) => !writable.has(row.customFieldId));
}

export function sameFieldRows(rows: FieldRow[], saved: LightspeedCustomFieldMapping[]): boolean {
  const key = (r: FieldRow) => `${r.customFieldId}\u0000${r.specLabel}`;
  const a = rows.map(key).sort();
  const b = saved.map((r) => key(r)).sort();
  return a.length === b.length && a.every((v, i) => v === b[i]);
}
